import React from 'react';
import {PaymentStatus, Item} from '../../../../../types/app.types';
import {LayoutDataHook} from '../Layout/Layout';
import {useControllerProps} from '../../ControllerContext';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {getSubscriptionDurationOld, isPriceZero} from './utils';
import {classes} from './ProductLineItemOverride.st.css';
import Download from 'wix-ui-icons-common/on-stage/Download';
import Calendar from 'wix-ui-icons-common/on-stage/Calendar';
import {ADD_TO_CALENDAR_ACTION_ID, MAX_PRODUCT_OPTION_LENGTH} from '../../../constants';
import {useStyles} from '@wix/tpa-settings/react';
import s from './ProductLineItem.scss';
import stylesParams from '../../../stylesParams';
import {OrderItemAction, PaymentOptionType} from '@wix/wixstores-graphql-schema-node';
import {ProductLineItemBase} from '../ProductLineItem/ProductLineItemBase';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CommonProductLineItem = ({
  item,
  withDivider,
}: {
  item: Item;
  withDivider: boolean;
  /* eslint-disable-next-line sonarjs/cognitive-complexity */
}) => {
  const {t} = useTranslation();
  const styleSettings = useStyles();
  const shouldRenderImage = styleSettings.get(stylesParams.THANK_YOU_PAGE_IMAGE_SELECTION);

  const toTitleValueOption = (o: Item['options'][0]) => ({title: o.title, value: o.description});
  const options = item.options
    ? item.options.filter((o) => !o.isFreeText || !!o.description.trim()).map(toTitleValueOption)
    : [];

  const {
    isSubscription,
    subscriptionName,
    subscriptionDuration,
    subscriptionFrequency,
    paymentStatus,
    reportClickOnLinkOnLineItem,
    subscriptionInfo,
    responsiveTYPCss,
    supportFreeTrialTYP,
    formattedPayAfterFreeTrialPrice,
    shouldShowMultipleLineItemActions,
    shouldShowMultipleSubscriptions,
    isItemSubtotalDiscountInclusive,
  } = useControllerProps().thankYouPageStore;

  const subscriptionInfoOld = isSubscription
    ? {
        name: subscriptionName,
        duration: getSubscriptionDurationOld({
          t,
          subscriptionDuration,
          subscriptionFrequency,
        }),
      }
    : undefined;

  const isPaidFor =
    (item.paymentOption === PaymentOptionType.FULL_PAYMENT_ONLINE && paymentStatus === PaymentStatus.PartiallyPaid) ||
    paymentStatus === PaymentStatus.Paid;
  const showDigitalProductDownloadLink = item.digitalFileLink && isPaidFor;
  const shouldHidePrice = item.renderingConfig?.hidePrice;
  const shouldHideQuantity = item.renderingConfig?.hideQuantity;
  const shouldShowPaymentTypeLabel = item.paymentOption && item.paymentOption !== PaymentOptionType.FULL_PAYMENT_ONLINE;

  const handleClickOnLineItemAction = (action: OrderItemAction) => {
    reportClickOnLinkOnLineItem(item, action);
  };
  const getPaymentTypeLabel = () => {
    const membershipName = item.selectedMembership?.name?.original;
    switch (item.paymentOption) {
      case PaymentOptionType.DEPOSIT_ONLINE:
        return t('thankyouPage.myCartPaymentMethod.deposit.lineItem', {price: item.formattedDepositAmount});
      case PaymentOptionType.MEMBERSHIP:
        return membershipName
          ? t('thankyouPage.myCartPaymentMethod.onlinePlan.lineItem', {membership: membershipName})
          : t('thankyouPage.myCartPaymentMethod.payWithPlan.lineItem');
      case PaymentOptionType.MEMBERSHIP_OFFLINE:
        return membershipName
          ? t('thankyouPage.myCartPaymentMethod.offlinePlan.lineItemWithId', {membership: membershipName})
          : t('thankyouPage.myCartPaymentMethod.offlinePlan.lineItemWithoutId');
      case PaymentOptionType.FULL_PAYMENT_OFFLINE:
        return item.price > 0 && t('thankyouPage.myCartPaymentMethod.offlinePayment.lineItem');
      /* istanbul ignore next */
      default:
        return '';
    }
  };

  const actions = getProductLineActions({
    item,
    onLineItemActionClick: handleClickOnLineItemAction,
    shouldShowMultipleLineItemActions,
  });

  const paymentTypeLabel = getPaymentTypeLabel();
  const lineItemPrice = item.lineItemPrice?.formattedAmount;
  const isLineItemTotalPriceZero = lineItemPrice && isPriceZero(lineItemPrice);
  const isitemPriceZero = item.formattedPrice && isPriceZero(item.formattedPrice);
  const modifiers = <ProductLineItemBase.Modifiers modifierGroups={item.modifierGroups} />;

  const shouldHideItemTotalPrice =
    (supportFreeTrialTYP && formattedPayAfterFreeTrialPrice && isLineItemTotalPriceZero) || shouldHidePrice;
  const shouldHideItemPrice =
    (supportFreeTrialTYP && formattedPayAfterFreeTrialPrice && isitemPriceZero) || shouldHidePrice;

  return (
    <div data-hook={LayoutDataHook.productLineItem} className={s.productLineItem} key={item.productId}>
      <ProductLineItemBase withDivider={withDivider} experiments={{responsiveTYPCss}}>
        {shouldRenderImage && (
          <ProductLineItemBase.Image
            product={{name: item.name, media: [item.media?.url ? item.media : null], productType: item.productType}}
          />
        )}
        <ProductLineItemBase.Name name={item.name} className={classes.myName} />
        {!shouldHideItemPrice && (
          <ProductLineItemBase.Price formattedPrice={item.formattedPrice} className={classes.myPrice} />
        )}
        {!shouldHideItemTotalPrice && (
          <ProductLineItemBase.TotalPrice lineItem={item} className={classes.myTotalPrice} />
        )}
        {shouldShowPaymentTypeLabel && (
          // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
          <ProductLineItemBase.PaymentTypeLabel label={paymentTypeLabel} />
        )}
        {isItemSubtotalDiscountInclusive && <ProductLineItemBase.Discounts lineItemId={item.id} />}
        <ProductLineItemBase.Options
          itemId={Number(item.productId)}
          options={options}
          className={classes.myOptions}
          maxOptionValLength={MAX_PRODUCT_OPTION_LENGTH}
        />
        {!shouldHideQuantity && (
          <ProductLineItemBase.ReadOnlyQuantity
            message={t('thankYouPage.quantity.label')}
            quantity={item.quantity}
            className={classes.myReadOnlyQuantity}
          />
        )}
        {!shouldShowMultipleSubscriptions && (subscriptionInfo || subscriptionInfoOld) && (
          <ProductLineItemBase.Subscription
            itemId={Number(item.productId)}
            subscriptionInfo={subscriptionInfo}
            subscription={subscriptionInfoOld}
            className={classes.mySubscription}
          />
        )}
        {shouldShowMultipleSubscriptions && item.subscriptionInfo && (
          <ProductLineItemBase.Subscription
            itemId={Number(item.productId)}
            subscriptionInfo={item.subscriptionInfo}
            subscription={subscriptionInfoOld}
            className={classes.mySubscription}
          />
        )}
        {showDigitalProductDownloadLink && (
          <ProductLineItemBase.Link
            title={t('thankYou.DOWNLOAD_LINK_LABEL')}
            icon={<Download className={classes.linkIcon} />}
            href={item.digitalFileLink ?? /* istanbul ignore next */ ''}
            onClick={() => handleClickOnLineItemAction({})}
            className={classes.myLink}
          />
        )}
        {modifiers}
        {actions}
      </ProductLineItemBase>
    </div>
  );
};

// TODO(dmytrokry@wix.com): remove this function when merging shouldShowMultipleLineItemActions FT
function getProductLineAction(item: Item, handleClickOnLineItem: (action: OrderItemAction) => void) {
  if (!item.actions?.length) {
    return null;
  }

  const action = item.actions[0];
  return (
    <ProductLineItemBase.Link
      key={action.actionId}
      title={action.label ?? /* istanbul ignore next */ ''}
      icon={
        action.actionId === ADD_TO_CALENDAR_ACTION_ID ? (
          <Calendar data-hook={LayoutDataHook.calendarIcon} className={classes.linkIcon} />
        ) : null
      }
      href={action.url ?? /* istanbul ignore next */ ''}
      onClick={() => handleClickOnLineItem(action)}
      className={classes.myLink}
    />
  );
}

// TODO(dmytrokry@wix.com): remove this function when merging shouldShowMultipleLineItemActions FT
function getProductLineActions({
  item,
  onLineItemActionClick,
  shouldShowMultipleLineItemActions,
}: {
  item: Item;
  onLineItemActionClick: (action: OrderItemAction) => void;
  shouldShowMultipleLineItemActions: boolean;
}) {
  if (!shouldShowMultipleLineItemActions) {
    return getProductLineAction(item, onLineItemActionClick);
  }

  return <ProductLineItemBase.Actions item={item} onLineItemActionClick={onLineItemActionClick} />;
}
